import { isIamDisabled } from '../api/marchApiEnv';
import { hasDevAccessToken } from './tokenStore';

export type AuthMode = 'default' | 'dev_keycloak' | 'dev_keycloak_password';

const LEGACY_AUTH_MODE_ALIASES: Record<string, AuthMode> = {
  local_keycloak: 'dev_keycloak',
  local_keycloak_password: 'dev_keycloak_password',
};

/** Frontend auth mode from Vite env. Production default unless explicitly set. */
export function getAuthMode(): AuthMode {
  const raw = (import.meta.env.VITE_AUTH_MODE ?? '').toString().trim().toLowerCase();
  if (raw === 'dev_keycloak_password') return 'dev_keycloak_password';
  if (raw === 'dev_keycloak') return 'dev_keycloak';
  const legacy = LEGACY_AUTH_MODE_ALIASES[raw];
  if (legacy) return legacy;
  return 'default';
}

/** OIDC redirect + PKCE login against development Keycloak. */
export function isDevKeycloakRedirectMode(): boolean {
  return !isIamDisabled() && getAuthMode() === 'dev_keycloak';
}

/** Username/password direct grant against development Keycloak. */
export function isDevKeycloakPasswordMode(): boolean {
  return !isIamDisabled() && getAuthMode() === 'dev_keycloak_password';
}

/** Any development IAM login screen (redirect or password). */
export function isDevIamLoginMode(): boolean {
  return isDevKeycloakRedirectMode() || isDevKeycloakPasswordMode();
}

/** @deprecated use {@link isDevKeycloakRedirectMode} */
export function isLocalKeycloakRedirectMode(): boolean {
  return isDevKeycloakRedirectMode();
}

/** @deprecated use {@link isDevKeycloakPasswordMode} */
export function isLocalKeycloakPasswordMode(): boolean {
  return isDevKeycloakPasswordMode();
}

/** @deprecated use {@link isDevIamLoginMode} */
export function isLocalIamLoginMode(): boolean {
  return isDevIamLoginMode();
}

/** @deprecated use {@link isDevIamLoginMode} */
export function isLocalKeycloakMode(): boolean {
  return isDevIamLoginMode();
}

/**
 * Skip the SMART launch requirement and open the clinical workspace directly.
 *
 * SECURITY BOUNDARY. The clinician view exists to answer questions about a
 * patient opened in the EHR; reaching it without a launch means reaching it
 * with no patient the clinician chose. This used to return true for ANY
 * dev-login session -- `requiresMarchAgentLogin() && hasDevAccessToken()` --
 * so simply signing in was enough to open the clinical workspace with no SMART
 * context at all. That is the hole this now closes.
 *
 * The bypass survives only for genuine local development, and now has to be
 * asked for:
 *
 *   1. `VITE_ALLOW_NON_SMART_CLINICAL=true` must be set explicitly, and
 *   2. the build must NOT be a production bundle (`import.meta.env.PROD` false).
 *
 * Both, not either. A production bundle ignores the flag entirely, so it
 * cannot be turned on by an environment variable at deploy time -- the value is
 * baked at build time and the DEV guard is what makes it unreachable in a
 * shipped artifact.
 *
 * `isIamDisabled()` still bypasses, because that switch already means "no
 * identity system at all" and is itself refused outside dev-class environments
 * by the backend (SEC-E1: MARCH_ENV_HANDLES_REAL_PHI closes it entirely).
 */
export function shouldBypassSmartLaunch(): boolean {
  if (isIamDisabled()) return true;
  return allowsNonSmartClinicalAccess();
}

/** True only in a non-production build that has explicitly opted out of the SMART requirement. */
export function allowsNonSmartClinicalAccess(): boolean {
  // Keyed on !PROD rather than DEV: a production bundle sets PROD=true, so the
  // opt-in is unreachable there no matter what the environment says. Reads
  // import.meta.env directly, the same way every other flag in this module
  // does -- vite statically replaces these at build time, which is exactly the
  // property that makes the guard un-flippable after the bundle is built.
  if (import.meta.env.PROD) return false;
  const raw = (import.meta.env.VITE_ALLOW_NON_SMART_CLINICAL ?? '').toString().trim().toLowerCase();
  return raw === 'true' || raw === '1';
}

/** MarchAgent Keycloak login required before workspace or protected March API calls. */
export function requiresMarchAgentLogin(): boolean {
  return isDevIamLoginMode();
}
